import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { LogIn, Loader2, KeyRound } from 'lucide-react'
import { useAuthStore } from '../core/stores/authStore'
import { SUPERADMIN_CODE } from '../App'
import api from '../core/services/api'

const FIXED_TENANT_CODE = 'crf'

export default function LoginPage() {
  const navigate = useNavigate()
  const { code } = useParams<{ code: string }>()
  const {
    login, isLoading, error, isAuthenticated, user,
    mustChangePassword, clearMustChangePassword,
    needsAziendaSelection
  } = useAuthStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Cambio password
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [changingPassword, setChangingPassword] = useState(false)
  const [changeError, setChangeError] = useState('')

  // Determina se è login SuperAdmin
  const isSuperadminMode = code?.toUpperCase() === SUPERADMIN_CODE
  const tenantCode = isSuperadminMode ? undefined : FIXED_TENANT_CODE

  useEffect(() => {
    localStorage.setItem('erp_tenant_code', FIXED_TENANT_CODE)
  }, [])

  // Redirect se già autenticato
  useEffect(() => {
    if (isAuthenticated && user && !mustChangePassword) {
      if (user.user_type === 'superadmin') {
        navigate('/superadmin')
      } else if (needsAziendaSelection) {
        navigate('/select-azienda')
      } else {
        navigate('/app')
      }
    }
  }, [isAuthenticated, user, mustChangePassword, needsAziendaSelection, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const success = await login(email, password, tenantCode)

    if (success) {
      const state = useAuthStore.getState()
      if (state.mustChangePassword) {
        return
      }
      if (state.user?.user_type === 'superadmin') {
        navigate('/superadmin')
      } else if (state.needsAziendaSelection) {
        navigate('/select-azienda')
      } else {
        navigate('/app')
      }
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setChangeError('')

    if (newPassword.length < 4) {
      setChangeError('La password deve essere di almeno 4 caratteri')
      return
    }
    if (newPassword !== confirmPassword) {
      setChangeError('Le password non corrispondono')
      return
    }

    setChangingPassword(true)
    try {
      await api.put('/v1/auth/cambia-password', { new_password: newPassword })
      clearMustChangePassword()
      const state = useAuthStore.getState()
      if (state.user?.user_type === 'superadmin') {
        navigate('/superadmin')
      } else if (state.needsAziendaSelection) {
        navigate('/select-azienda')
      } else {
        navigate('/app')
      }
    } catch (err: any) {
      setChangeError(err.response?.data?.detail || 'Errore nel cambio password')
    } finally {
      setChangingPassword(false)
    }
  }

  // Form cambio password obbligatorio
  if (mustChangePassword && isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 border border-gray-200">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Cambio Password Obbligatorio</h1>
            <p className="text-sm text-gray-500 mt-2">
              Per motivi di sicurezza devi impostare una nuova password prima di continuare.
            </p>
          </div>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nuova Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Inserisci nuova password"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
                required
                autoFocus
                style={{ fontSize: '16px' }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Conferma Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ripeti la nuova password"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
                required
                style={{ fontSize: '16px' }}
              />
            </div>

            {changeError && (
              <div className="bg-red-50 text-red-600 text-sm px-4 py-2 rounded-lg border border-red-100">
                {changeError}
              </div>
            )}

            <button
              type="submit"
              disabled={changingPassword}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {changingPassword ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <KeyRound className="w-5 h-5" />
              )}
              {changingPassword ? 'Salvataggio...' : 'Imposta Password'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 border border-gray-200">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="CRF"
            className="w-20 h-20 rounded-2xl mx-auto mb-4 shadow-lg"
          />
          <h1 className="text-2xl font-bold text-gray-900">CRF</h1>
          {isSuperadminMode && (
            <span className="inline-flex items-center px-3 py-1 mt-2 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
              Amministrazione Sistema
            </span>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@email.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
              required
              style={{ fontSize: '16px' }}
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm px-4 py-2 rounded-lg border border-red-100">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <LogIn className="w-5 h-5" />
            )}
            {isLoading ? 'Accesso in corso...' : 'Accedi'}
          </button>
        </form>

        <p className="text-center text-gray-400 text-sm mt-6">
          CRF v1.0.0
        </p>
      </div>
    </div>
  )
}
